/**
 * Caps a list's height at its first N children and lets the rest scroll.
 *
 * Measured rather than hard-coded in pixels: the Etusivu lists mix card
 * heights (a two-line task title makes a taller card), so a fixed max-height
 * would cut a row in half. Returns the ref to put on the scroll container and
 * the max-height to apply — undefined while the list is short enough to need
 * no scrolling at all.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export function useVisibleRowsHeight(visibleRows: number, itemCount: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rows = Array.from(el.children) as HTMLElement[];
    if (rows.length <= visibleRows) {
      setMaxHeight(undefined);
      return;
    }
    const first = rows[0];
    const last = rows[visibleRows - 1];
    if (!first || !last) return;
    setMaxHeight(last.offsetTop + last.offsetHeight - first.offsetTop);
  }, [visibleRows]);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    for (const child of Array.from(el.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [measure, itemCount]);

  return { ref, maxHeight };
}
